import { apiGet, apiPost } from './client';
import type { OperationResult } from './types';
import type { ObligationReading, ResultState, RunIdentity, RunReading, TurnRuntimeEvidence, TurnTimeline } from './runEvidence';

export interface RunSummary {
  id: number;
  aiAgentId: number;
  agentName?: string | null;
  validationTestSuiteId: number;
  suiteSlug?: string | null;
  runDate: string;
  completedDate?: string | null;
  totalElapsedMs: number;
  status?: string;
  runType?: string;
  environment?: string;
  triggerSource?: string;
  gitBranch?: string;
  gitCommitSha?: string;
  totalScenarios: number;
  passedScenarios: number;
  failedScenarios: number;
  overallPassRate: number;
  averageScore: number;
  deploymentBlocked: boolean;
  safetyOnly: boolean;
}

export interface ScenarioResultSummary {
  id: number;
  scenarioId: string;
  passed: boolean;
  isSafety: boolean;
  isCritical: boolean;
  score: number;
  passCount: number;
  totalChecks: number;
  turnCount: number;
  failedAssertionCount: number;
  elapsedMs: number;
  error?: string;
  // Phase 1d additions — populated when the run was captured under the new
  // framework. Older results have these as null.
  scenarioSnapshotId?: number | null;
  estimatedCostUsd?: number | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
}

export interface RunDetail {
  run: RunSummary;
  results: ScenarioResultSummary[];
  /** SI-03b: states, identity, cost by role and time. Null when it could not be read (readingProblem says why). */
  reading?: RunReading | null;
  readingProblem?: string | null;
}

export interface RunSearchParams {
  agentId?: number;
  environment?: string;
  status?: string;
  days?: number;
  pageSize?: number;
}

export async function searchRuns(params: RunSearchParams = {}): Promise<RunSummary[]> {
  const wrapped = await apiGet<OperationResult<RunSummary[]>>(
    '/api/ValidationRuns/Search',
    {
      ...(params.agentId ? { agentId: params.agentId } : {}),
      ...(params.environment ? { environment: params.environment } : {}),
      ...(params.status ? { status: params.status } : {}),
      days: params.days ?? 30,
      pageSize: params.pageSize ?? 50,
    }
  );
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load runs');
  return wrapped.data ?? [];
}

export async function getRunById(id: number): Promise<RunDetail> {
  const wrapped = await apiGet<OperationResult<RunDetail>>(`/api/ValidationRuns/GetById/${id}`);
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || `Run ${id} not found`);
  }
  return wrapped.data;
}

// ─────────────────────────────────────────────────────────────────────────────
// Compare two runs of one agent (EVAL-06 STUDIO-EVAL-06). The server pairs results by scenario and decides each
// change; a scenario whose version changed is "NotComparable", never better or worse. Counts are taken from
// `scenarios` on the client (dictionary keys are camel-cased by the client and would not match the change names).
// ─────────────────────────────────────────────────────────────────────────────

export type ScenarioChange =
  | 'NewlyFailing'
  | 'Resolved'
  | 'StillFailing'
  | 'Inconclusive'
  | 'NotComparable'
  | 'MissingFromCandidate'
  | 'NewInCandidate'
  | 'StillPassing';

/** SI-07: one execution of a scenario in a run. Every trial is shown; none is chosen. */
export interface ComparedTrial {
  repeat: number;
  resultId: number;
  outcome: 'Pass' | 'Fail' | 'DidNotComplete';
  /** "Trial 2: Fail" — the dot's words on hover, focus and for a screen reader. */
  words: string;
}

export interface ComparedResult {
  resultId: number;
  /** Every trial completed and passed. */
  passed: boolean;
  /** The side has a verdict: every trial completed, or a completed trial failed. */
  completed: boolean;
  scenarioSnapshotId?: number | null;
  failedAssertionCount: number;
  /** SI-07: every result of the scenario in the run, oldest first. */
  trials?: ComparedTrial[] | null;
  /** "3 of 3 passed", "1 passed · 1 failed · 1 did not complete". */
  summary?: string | null;
}

export interface ScenarioComparisonRow {
  scenarioId: string;
  change: ScenarioChange;
  reason: string;
  baseline?: ComparedResult | null;
  candidate?: ComparedResult | null;
}

// SI-07 (plan §7): what a comparison says was allowed to change, and whether the pair is a fair test of it. The server
// decides every verdict and writes every sentence (ValidationRunComparisonRules); the page only lays them out.
export type ComparisonType = 'AgentChange' | 'ModelChange' | 'CombinedChange' | 'EvaluatorChange';
export type ComparabilityVerdict = 'Comparable' | 'Confounded' | 'NotComparable' | 'Unavailable';
export type ControlState = 'Held' | 'Changed' | 'NotRecorded' | 'NotYetRecorded';

export interface ComparisonControl {
  key: string;
  label: string;
  state: ControlState;
  baseline: string;
  candidate: string;
  note?: string | null;
  /** The chosen type lets this differ (a model change: the configured model and the deployment). */
  allowedToChange?: boolean;
}

export interface ComparisonTypeOption {
  type: ComparisonType;
  label: string;
  selectable: boolean;
  reason?: string | null;
}

export interface ComparabilityWarning {
  key: string;
  headline: string;
  detail: string;
  action?: string | null;
}

export interface Comparability {
  type: ComparisonType;
  inferred?: ComparisonType | null;
  verdict: ComparabilityVerdict;
  headline: string;
  detail?: string | null;
  brokenControl?: string | null;
  action?: string | null;
  controls: ComparisonControl[];
  types: ComparisonTypeOption[];
  warnings: ComparabilityWarning[];
}

export interface PairingSummary {
  scenariosInEither: number;
  scenariosInBoth: number;
  versionsMatched: number;
  scenariosEdited: number;
  versionNotRecorded: number;
  baselineOnly: number;
  candidateOnly: number;
  baselineTrials: number;
  candidateTrials: number;
  oneResultPerScenario: boolean;
  pairedBy: string;
  worldVersions: string;
  baselineIncomplete: number;
  candidateIncomplete: number;
}

/** Agent · Candidate revision · Actual deployment — never a display name alone. */
export interface ComparisonSide {
  runId: number;
  runDate: string;
  status?: string | null;
  merchantId?: number | null;
  agentId: number;
  agentName?: string | null;
  candidateHash?: string | null;
  revision: string;
  isLive?: boolean | null;
  candidateChanges: string[];
  configuredModel?: string | null;
  configuredModelId?: number | null;
  configuredDeployment?: string | null;
  effectiveModel?: string | null;
  /** What answered the agent's rounds, with how many each (the run page's DeploymentCount); a null deployment was not named. */
  deployments: { deployment: string | null; calls: number }[];
  agentRounds: number;
  answeredBy: string;
  answeringRecorded: boolean;
  selectedMatchesActual?: boolean | null;
  evaluator: string;
  evaluatorVersion?: string | null;
  trials: number;
}

export interface PricedPortion {
  pricedUsd: number;
  pricedCalls: number;
  unpricedCalls: number;
}

export interface UsageLine {
  key: 'agent' | 'peers' | 'router' | 'other' | 'evaluator' | string;
  label: string;
  calls: number;
  pricedCalls: number;
  unpricedCalls: number;
  pricedUsd: number;
  medianMs?: number | null;
  p90Ms?: number | null;
  /** The server's words: "$0.97 · 81 of 81 calls priced", "Price unavailable: 36 calls, no rate on file". */
  words: string;
  note?: string | null;
}

export interface RunUsage {
  recorded: boolean;
  notRecordedReason?: string | null;
  lines: UsageLine[];
  trials: number;
  trialMedianMs?: number | null;
  trialP90Ms?: number | null;
  whole: PricedPortion;
  /** "Recorded priced usage: $1.41, plus 36 unpriced calls" — never $0 for an unpriced call. */
  wholeWords: string;
}

export interface RunComparison {
  baselineRunId: number;
  candidateRunId: number;
  agentId: number;
  scenarios: ScenarioComparisonRow[];
  notes: string[];
  // SI-07 — absent from a server older than SI-07; the drawer then shows the scenarios only.
  type?: ComparisonType;
  comparability?: Comparability | null;
  pairing?: PairingSummary | null;
  baselineIdentity?: ComparisonSide | null;
  candidateIdentity?: ComparisonSide | null;
  baselineUsage?: RunUsage | null;
  candidateUsage?: RunUsage | null;
  /** Shown only where predefined release criteria exist for the intended use; null today. */
  releaseCriteria?: string | null;
  sampleStatement?: string | null;
}

export async function compareRuns(
  baselineRunId: number,
  candidateRunId: number,
  signal?: AbortSignal,
  type?: ComparisonType | null,
): Promise<RunComparison> {
  const wrapped = await apiGet<OperationResult<RunComparison>>(
    '/api/ValidationRuns/Compare',
    type ? { baselineRunId, candidateRunId, type } : { baselineRunId, candidateRunId },
    { signal },
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'These runs could not be compared');
  }
  return wrapped.data;
}

/** One run the picker offers against a baseline, with the type it would make and — before it is chosen — why it is held back. */
export interface ComparableRun {
  identity: ComparisonSide;
  type?: ComparisonType | null;
  verdict: ComparabilityVerdict;
  summary: string;
  heldBack: boolean;
  heldBackReason?: string | null;
}

export interface ComparableRunsView {
  baseline: ComparisonSide;
  runs: ComparableRun[];
  otherPracticeRuns: number;
  note?: string | null;
}

export async function listComparableRuns(
  baselineRunId: number,
  type?: ComparisonType | null,
  signal?: AbortSignal,
): Promise<ComparableRunsView> {
  const wrapped = await apiGet<OperationResult<ComparableRunsView>>(
    '/api/ValidationRuns/Compare/Runs',
    type ? { baselineRunId, type } : { baselineRunId },
    { signal },
  );
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'The runs could not be listed');
  return wrapped.data;
}

/** A saved comparison (scenario-intelligence sql/006), re-computed on its stored evidence when opened. */
export interface SavedComparison {
  id: number;
  title?: string | null;
  type: ComparisonType;
  savedAtUtc: string;
  savedBy: number;
  verdictWhenSaved?: string | null;
  /** Set when the verdict or counts moved since saving. */
  changedSinceSaved?: string | null;
  comparison: RunComparison;
}

export async function saveComparison(
  body: { baselineRunId: number; candidateRunId: number; type?: ComparisonType | null; title?: string | null },
): Promise<SavedComparison> {
  const wrapped = await apiPost<OperationResult<SavedComparison>>('/api/ValidationRuns/Comparisons', body);
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'The comparison could not be saved');
  return wrapped.data;
}

export async function getSavedComparison(id: number, signal?: AbortSignal): Promise<SavedComparison> {
  const wrapped = await apiGet<OperationResult<SavedComparison>>(`/api/ValidationRuns/Comparisons/${id}`, undefined, { signal });
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || `Comparison #${id} could not be opened`);
  return wrapped.data;
}

// ─────────────────────────────────────────────────────────────────────────────
// Agent evaluation overview (EVAL-06 STUDIO-EVAL-05). "Is this agent, as it is now, doing its job?" Every
// judgement is the server's (EvaluationEvidenceRules); a result only counts when it tested the agent's CURRENT
// version on the scenario's CURRENT snapshot within the freshness window. The headline never says "safe".
// ─────────────────────────────────────────────────────────────────────────────

export type EvaluationHeadline = 'NeedsAttention' | 'EvidenceIncomplete' | 'EligibleForReleaseReview';
/**
 * AwaitingReview (4 Nov 2026): every automatic check passed on this version, but it holds a check a reviewer judges that
 * nobody has judged; never a pass, and a required one leaves the evidence incomplete.
 */
export type ScenarioStanding = 'Passed' | 'Failed' | 'Inconclusive' | 'NotTested' | 'Quarantined' | 'Retired' | 'AwaitingReview';

export interface ScenarioStandingRow {
  scenarioId: string;
  title?: string | null;
  category?: string | null;
  isSafety: boolean;
  required: boolean;
  standing: ScenarioStanding;
  reason: string;
  resultId?: number | null;
  runId?: number | null;
  runUtc?: string | null;
  latestAnyVersionResultId?: number | null;
  latestAnyVersionRunUtc?: string | null;
  latestAnyVersionPassed?: boolean | null;
  currentSnapshotId?: number | null;
  /** A reviewer approved what the scenario's CURRENT version expects. */
  expectationApproved?: boolean;
  /** Review state of the failing or inconclusive result shown, when there is one. */
  finding?: { disposition: FindingDisposition; assigneeUserId?: number | null; cause?: FindingCause | null; events: number } | null;
}

export interface AgentEvaluationOverview {
  agentId: number;
  agentName: string;
  headline: EvaluationHeadline;
  reasons: string[];
  currentCandidateHash?: string | null;
  untestedChanges: string[];
  latestRunId?: number | null;
  latestRunUtc?: string | null;
  unfinishedRequests: number;
  policyVersion: string;
  scenarios: ScenarioStandingRow[];
}

export async function getAgentEvaluationOverview(agentId: number, signal?: AbortSignal): Promise<AgentEvaluationOverview> {
  const wrapped = await apiGet<OperationResult<AgentEvaluationOverview>>(
    `/api/ValidationRuns/Agents/${agentId}/Overview`,
    undefined,
    { signal },
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || `Could not load the evaluation of agent ${agentId}`);
  }
  return wrapped.data;
}

// ─────────────────────────────────────────────────────────────────────────────
// Finding review, scenario approval and release (EVAL-06 / RELEASE-01, sql/008). Every rule is the server's;
// these calls append one record each and return the new state. Nothing is ever edited or deleted.
// ─────────────────────────────────────────────────────────────────────────────

export type FindingAction = 'Assign' | 'Note' | 'Classify' | 'Resolve' | 'Reopen' | 'Dispute';
export type FindingCause = 'Agent' | 'Scenario' | 'Evaluator' | 'Infrastructure';
export type FindingDisposition = 'Open' | 'Acknowledged' | 'Resolved' | 'Disputed';

export interface FindingEvent {
  id: number;
  action: FindingAction;
  assigneeUserId?: number | null;
  assigneeName?: string | null;
  classification?: FindingCause | null;
  note?: string | null;
  correctionRef?: string | null;
  retestResultId?: number | null;
  byUserId: number;
  byName?: string | null;
  atUtc: string;
}

export interface FindingReview {
  resultId: number;
  agentId: number;
  scenarioId: string;
  runId: number;
  disposition: FindingDisposition;
  assigneeUserId?: number | null;
  assigneeName?: string | null;
  cause?: FindingCause | null;
  retestResultId?: number | null;
  events: FindingEvent[];
}

export interface FindingReviewInput {
  action: FindingAction;
  /** Assign: omit to assign to yourself. */
  assigneeUserId?: number;
  classification?: FindingCause;
  note?: string;
  correctionRef?: string;
  retestResultId?: number;
}

async function unwrap<T>(p: Promise<OperationResult<T>>, fallback: string): Promise<T> {
  const wrapped = await p;
  if (!wrapped.isSuccess || wrapped.data == null) throw new Error(wrapped.message || fallback);
  return wrapped.data;
}

export const getFindingReview = (resultId: number, signal?: AbortSignal) =>
  unwrap(apiGet<OperationResult<FindingReview>>(`/api/ValidationRuns/Results/${resultId}/Review`, undefined, { signal }),
    'Could not load the review');

export const addFindingReview = (resultId: number, input: FindingReviewInput) =>
  unwrap(apiPost<OperationResult<FindingReview>>(`/api/ValidationRuns/Results/${resultId}/Review`, input),
    'Could not record that');

export type ScenarioApprovalDecision = 'Approved' | 'ChangesRequested' | 'Withdrawn';

export const decideScenario = (
  agentId: number,
  body: { scenarioId: string; scenarioSnapshotId: number; decision: ScenarioApprovalDecision; reviewerRole?: string; justification: string },
) =>
  unwrap(apiPost<OperationResult<{ id: number }>>(`/api/ValidationRuns/Agents/${agentId}/Scenarios/Approval`, body),
    'Could not record the review');

export type ReleaseDecisionKind = 'Approved' | 'Rejected' | 'Exception' | 'Revoked';
export type ReleaseState =
  | 'NotDecided'
  | 'Approved'
  | 'Exception'
  | 'ExceptionExpired'
  | 'Rejected'
  | 'Revoked'
  | 'ApprovalNoLongerHolds'
  | 'ExceptionNoLongerCovers';

export interface ReleaseChecklistItem {
  key: string;
  requirement: string;
  met: boolean;
  detail: string;
}

export interface ReleaseDecisionRow {
  id: number;
  decision: ReleaseDecisionKind;
  candidateHash: string;
  reason: string;
  expiresUtc?: string | null;
  followUp?: string | null;
  byUserId: number;
  byName?: string | null;
  atUtc: string;
  policyVersion: string;
}

export interface AgentRelease {
  agentId: number;
  currentCandidateHash?: string | null;
  policyVersion: string;
  eligible: boolean;
  checklistItems: ReleaseChecklistItem[];
  state: ReleaseState;
  currentDecision?: ReleaseDecisionRow | null;
  changedSinceApprovedVersion: string[];
  history: ReleaseDecisionRow[];
  /** Why the current decision no longer holds (the evidence changed since it was recorded), when it does not. */
  noLongerHoldsReason?: string | null;
}

export const getAgentRelease = (agentId: number, signal?: AbortSignal) =>
  unwrap(apiGet<OperationResult<AgentRelease>>(`/api/ValidationRuns/Agents/${agentId}/Release`, undefined, { signal }),
    'Could not load the release checklist');

export const decideRelease = (
  agentId: number,
  body: { decision: ReleaseDecisionKind; reason: string; expiresUtc?: string; followUp?: string; expectedCandidateHash?: string | null },
) =>
  unwrap(apiPost<OperationResult<AgentRelease>>(`/api/ValidationRuns/Agents/${agentId}/Release`, body),
    'Could not record the decision');

// ─────────────────────────────────────────────────────────────────────────────
// Evidence for one scenario result — the finding drawer (EVAL-06).
//
// Mirrors OO.Business.AIInfo.Validation.ValidationResultEvidence. Read-only.
// `completeness.notes` says in plain words what the record cannot show; the UI
// prints them as given and never fills a gap in.
// ─────────────────────────────────────────────────────────────────────────────

export interface FailureEvidence {
  check: string;
  detail: string;
}

export interface ToolCallEvidence {
  order: number;
  toolName: string;
  label?: string | null;
  request?: string | null;
  requestTruncated: boolean;
  response?: string | null;
  responseTruncated: boolean;
  /** True when the run answered the call itself and the tool did not run (the agent's own change-or-send tools never run in a test). */
  simulated?: boolean;
  /** SI-03a: how long it ran, how it ended (Completed, Simulated, NoResult) and when it started. */
  durationMs?: number | null;
  outcome?: string | null;
  startedUtc?: string | null;
}

export interface RoutedSkillEvidence {
  name: string;
  reason?: string | null;
  alwaysLoad: boolean;
}

export interface RouterEvidence {
  status?: string | null;
  model?: string | null;
  elapsedMs: number;
  failureReason?: string | null;
  skills: RoutedSkillEvidence[];
}

export interface TurnEvidence {
  turnIndex: number;
  userMessage?: string | null;
  agentReply?: string | null;
  elapsedMs: number;
  error?: string | null;
  /** null = no check was recorded for this turn (never read it as a pass). */
  assertionsPassed?: boolean | null;
  failures: FailureEvidence[];
  toolCalls: ToolCallEvidence[];
  router?: RouterEvidence | null;
  /** SI-03a: the turn's trace; null on turns recorded before it existed. */
  correlationId?: string | null;
  runtime?: TurnRuntimeEvidence | null;
  startedUtc?: string | null;
  /** SI-03b: the turn in observed order, for the Debugger. */
  timeline?: TurnTimeline | null;
}

export interface RuleEvidence {
  rule: string;
  passed: boolean;
  failures: FailureEvidence[];
}

export interface EvidenceCompleteness {
  scenarioVersionKnown: boolean;
  safetyClassified: boolean;
  allTurnsRecorded: boolean;
  allTurnsChecked: boolean;
  notes: string[];
}

export interface ValidationResultEvidence {
  resultId: number;
  validationRunId: number;
  agentId: number;
  scenarioId: string;
  scenarioSnapshotId?: number | null;
  passed: boolean;
  traceError?: string | null;
  turnCount: number;
  failedAssertionCount: number;
  elapsedMs: number;
  turns: TurnEvidence[];
  conversationRules: RuleEvidence[];
  completeness: EvidenceCompleteness;
  /** SI-03b: every check of the version that ran, as expected against observed. */
  obligations?: ObligationReading | null;
  identity?: RunIdentity | null;
  /**
   * The result's state as the server classifies it (RunReadingRules.Classify), the same reading the run page and the
   * Overview show. Absent from APIs before 4 Nov 2026.
   */
  state?: ResultState | null;
}

export async function getResultEvidence(
  resultId: number,
  signal?: AbortSignal,
): Promise<ValidationResultEvidence> {
  const wrapped = await apiGet<OperationResult<ValidationResultEvidence>>(
    `/api/ValidationRuns/Results/${resultId}/Evidence`,
    undefined,
    { signal },
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || `Result ${resultId} not found`);
  }
  return wrapped.data;
}

// ─────────────────────────────────────────────────────────────────────────────
// Enqueue — fire-and-return trigger for a validation run.
//
// Two modes share the same endpoint:
//   * Batch     — omit scenarioId; runs every active scenario for the agent.
//   * Per-scenario — pass scenarioId; runs only that one (still produces a
//                    real ValidationRun row in run history).
//
// The endpoint returns immediately with a correlationId. The actual run
// happens asynchronously in the QueueProcessingContainerApp container —
// poll Recent Runs (or the agent's run history) to see the new row land.
// ─────────────────────────────────────────────────────────────────────────────

export interface EnqueueRunRequest {
  agentId: number;
  /** Optional. Single-scenario mode when present — runs just this one. */
  scenarioId?: string;
  agentDisplayName?: string;
  environment?: string;
  triggerSource?: string;
  suiteSlug?: string;
  suiteName?: string;
  audienceType?: string;
  notes?: string;
  /**
   * Optional. Run at this practice — any practice. Omit to run each scenario as written
   * (demo practice, the scenario's own patient). The server refuses a patient without a practice.
   */
  merchantId?: number;
  /** Optional. A patient of `merchantId` used by every scenario in the run. */
  memberId?: number;
  /**
   * One key per click (see `newRequestKey`). Sending the same key again returns the same job instead of
   * starting a second run, so a retried or doubled submit never runs twice.
   */
  idempotencyKey?: string;
  /**
   * SI-07 candidate seam. Run a candidate version of the agent that differs only in its configured model: this
   * ai.AIModel row is pinned for the agent under test; nothing is activated or written. Omit for the live agent.
   */
  candidateModelId?: number;
}

export interface EnqueueRunResponse {
  correlationId: string;
  agentId: number;
  message: string;
  jobId?: number;
  state?: string;
  /** 'Created' | 'Duplicate' */
  outcome?: string;
}

export async function enqueueValidationRun(
  request: EnqueueRunRequest
): Promise<EnqueueRunResponse> {
  const wrapped = await apiPost<OperationResult<EnqueueRunResponse>>(
    '/api/ValidationRuns/Enqueue',
    request,
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to enqueue validation run');
  }
  return wrapped.data;
}

/** A fresh key for one click of a Run button. Make it when the confirm opens and send it with every retry of that click. */
export function newRequestKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Run requests (jobs) — EVAL-06. Every Run click is a job, written before the queue send. The job says
// whether the request is waiting, running (and how far), cancelled, refused, failed or done, and links the
// run it produced. States are the server's; the screen only words them.
// ─────────────────────────────────────────────────────────────────────────────

export type ValidationRunJobState =
  | 'DispatchPending'
  | 'Queued'
  | 'Running'
  | 'CancelRequested'
  | 'Completed'
  | 'Refused'
  | 'Cancelled'
  | 'Failed';

export interface ValidationRunJob {
  id: number;
  agentId: number;
  scenarioId?: string | null;
  merchantId?: number | null;
  memberId?: number | null;
  /** 'Run' | 'Rerun' */
  kind: string;
  parentJobId?: number | null;
  triggerSource?: string | null;
  environment?: string | null;
  state: ValidationRunJobState;
  refusalReason?: string | null;
  failureReason?: string | null;
  queuedUtc?: string | null;
  startedUtc?: string | null;
  finishedUtc?: string | null;
  heartbeatUtc?: string | null;
  expectedTrials?: number | null;
  completedTrials: number;
  validationRunId?: number | null;
  submittedUtc: string;
}

/** A job that has not ended yet (mirrors ValidationRunJobRules.FinishedStates). */
export function isJobUnfinished(state: ValidationRunJobState): boolean {
  return state !== 'Completed' && state !== 'Refused' && state !== 'Cancelled' && state !== 'Failed';
}

/** One run request (job): its state, progress and the run it produced. */
export async function getRunJob(jobId: number, signal?: AbortSignal): Promise<ValidationRunJob> {
  const wrapped = await apiGet<OperationResult<ValidationRunJob>>(`/api/ValidationRuns/Jobs/${jobId}`, undefined, { signal });
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || `Run request ${jobId} not found`);
  return wrapped.data;
}

export async function listRunJobs(agentId: number, take = 20, signal?: AbortSignal): Promise<ValidationRunJob[]> {
  const wrapped = await apiGet<OperationResult<ValidationRunJob[]>>(
    '/api/ValidationRuns/Jobs',
    { agentId, take },
    { signal },
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to load run requests');
  }
  return wrapped.data;
}

export interface JobActionResponse {
  message: string;
  job?: ValidationRunJob | null;
}

export async function cancelRunJob(jobId: number): Promise<JobActionResponse> {
  const wrapped = await apiPost<OperationResult<JobActionResponse>>(`/api/ValidationRuns/Jobs/${jobId}/Cancel`, {});
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || `Failed to cancel job ${jobId}`);
  }
  return wrapped.data;
}

export async function rerunJob(jobId: number, idempotencyKey: string): Promise<JobActionResponse> {
  const wrapped = await apiPost<OperationResult<JobActionResponse>>(`/api/ValidationRuns/Jobs/${jobId}/Rerun`, {
    idempotencyKey,
  });
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || `Failed to run job ${jobId} again`);
  }
  return wrapped.data;
}

// ─────────────────────────────────────────────────────────────────────────────
// HTML report metadata for a validation run.
//
// One ValidationRun has at most one ValidationReport. The shape mirrors the
// MemberAttachment pattern: SQL holds the SAS URL, the bytes live in blob
// storage. The frontend loads the URL directly into an iframe — no proxy.
//
// Returns 404-equivalent (isSuccess=false / data=null) when the run pre-dates
// the cloud-report cutover or when persistence skipped the upload.
// ─────────────────────────────────────────────────────────────────────────────

export interface ValidationReportMeta {
  id: number;
  validationRunId: number;
  merchantId: number;
  container: string;
  blobId: string;
  /** Full pre-signed SAS URL — embed directly in iframe / open in new tab. */
  blobWithSharedAccessKey: string;
  title?: string | null;
  fileExtension: string;
  sizeBytes?: number | null;
  scenarioCount?: number | null;
}

export async function getValidationReportByRunId(
  runId: number,
): Promise<ValidationReportMeta | null> {
  const wrapped = await apiGet<OperationResult<ValidationReportMeta>>(
    `/api/Validation/Reports/ByRun/${runId}`,
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    return null; // Treat "no report" as a benign empty state, not an error.
  }
  return wrapped.data;
}

// ─────────────────────────────────────────────────────────────────────────────
// Analytics summary — drives the Health Checks dashboard panels.
// One round-trip per dashboard load; backend aggregates everything in-memory
// from a single SQL pull, so adding panels here is cheap.
// ─────────────────────────────────────────────────────────────────────────────

export interface TopFailingScenario {
  scenarioId: string;
  agentId: number;
  agentName: string;
  failCount: number;
  totalRuns: number;
  /** 0..100 (decimal, rounded to 1 dp on the server). */
  failRate: number;
  lastFailDate: string;
}

export interface CostByAgent {
  agentId: number;
  agentName: string;
  costUsd: number;
  runCount: number;
}

export interface CostSummary {
  totalUsd: number;
  avgCostPerRunUsd: number;
  inputTokens: number;
  outputTokens: number;
  runsWithCost: number;
  byAgent: CostByAgent[];
}

export interface FlakeCell {
  runDate: string;
  passed: boolean;
  validationRunId: number;
}

export interface FlakeRow {
  scenarioId: string;
  agentId: number;
  agentName: string;
  /** Chronological — oldest first, newest last. UI renders left-to-right. */
  cells: FlakeCell[];
}

export interface AnalyticsSummary {
  topFailingScenarios: TopFailingScenario[];
  cost: CostSummary;
  flake: FlakeRow[];
}

export async function getValidationAnalyticsSummary(
  days: number,
): Promise<AnalyticsSummary> {
  const wrapped = await apiGet<OperationResult<AnalyticsSummary>>(
    '/api/Validation/Analytics/Summary',
    { days },
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to load analytics');
  }
  return wrapped.data;
}
